import { getActiveRestaurantId, getActiveStoreId, isSupermarketContext } from './entityContext';

export const BUY_GROCERY_PATH = '/purchase/buy-grocery';
export const BUY_GROCERY_CART_PATH = '/purchase/buy-grocery/cart';
export const GROCERY_GST_PERCENT = 5;
export const GROCERY_PLATFORM_FEE = 10;

function roundMoney(amount) {
  return Math.round(Number(amount) * 100) / 100;
}

/** Active restaurant or store context for buy-grocery cart and checkout. */
export function getBuyGroceryBuyerContext() {
  if (isSupermarketContext()) {
    const storeId = Number(getActiveStoreId() || 0);
    return { isStore: true, buyerId: storeId, entityType: 'store' };
  }
  const restaurantId = Number(getActiveRestaurantId() || 0);
  return { isStore: false, buyerId: restaurantId, entityType: 'restaurant' };
}

export function cartSubtotalFromCart(cart) {
  return cartTotalFromCart(cart);
}

export function cartGstFromSubtotal(subtotal, gstPercent = GROCERY_GST_PERCENT) {
  const base = Number(subtotal) || 0;
  if (base <= 0) return 0;
  return roundMoney(base * Number(gstPercent) / 100);
}

export function cartPlatformFeeFromSubtotal(
  subtotal,
  platformFee = GROCERY_PLATFORM_FEE
) {
  const base = Number(subtotal) || 0;
  if (base <= 0) return 0;
  return roundMoney(platformFee);
}

export function cartGrandTotalFromCart(
  cart,
  { gstPercent = GROCERY_GST_PERCENT, platformFee = GROCERY_PLATFORM_FEE } = {}
) {
  const subtotal = cartSubtotalFromCart(cart);
  const gst = cartGstFromSubtotal(subtotal, gstPercent);
  const platform = cartPlatformFeeFromSubtotal(subtotal, platformFee);
  return roundMoney(subtotal + gst + platform);
}

export function buildStoreOrderPayload(group, { gstPercent = GROCERY_GST_PERCENT } = {}) {
  const subtotal = Number(group.subtotal) || 0;
  const gst = cartGstFromSubtotal(subtotal, gstPercent);
  return {
    storeId: Number(group.storeId),
    grandTotal: roundMoney(subtotal + gst),
    lineItems: (group.lines || []).map((line) => ({
      groceryItemId: line.groceryItemId,
      itemName: line.itemName,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      gstPercent,
    })),
  };
}

function storageKey(buyer) {
  const ctx = buyer?.isStore != null ? buyer : getBuyGroceryBuyerContext();
  if (ctx.isStore) {
    return `buyGroceryCart_store_${Number(ctx.buyerId) || 0}`;
  }
  return `buyGroceryCart_restaurant_${Number(ctx.buyerId) || 0}`;
}

export function loadBuyGroceryCart(buyer = getBuyGroceryBuyerContext()) {
  try {
    const raw = localStorage.getItem(storageKey(buyer));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function saveBuyGroceryCart(buyer, cart) {
  const ctx = buyer?.isStore != null ? buyer : getBuyGroceryBuyerContext();
  const id = Number(ctx.buyerId) || 0;
  if (!id) return;
  if (!cart || Object.keys(cart).length === 0) {
    localStorage.removeItem(storageKey(ctx));
    return;
  }
  localStorage.setItem(storageKey(ctx), JSON.stringify(cart));
}

export function clearBuyGroceryCartStorage(buyer = getBuyGroceryBuyerContext()) {
  localStorage.removeItem(storageKey(buyer));
}

export function cartLinesFromCart(cart) {
  return Object.values(cart || {});
}

export function cartCountFromCart(cart) {
  return cartLinesFromCart(cart).reduce((sum, line) => sum + Number(line.quantity || 0), 0);
}

export function cartTotalFromCart(cart) {
  return cartLinesFromCart(cart).reduce(
    (sum, line) => sum + Number(line.quantity || 0) * Number(line.unitPrice || 0),
    0
  );
}

export function groupCartByStore(cartLines) {
  const groups = {};
  (cartLines || []).forEach((line) => {
    const key = String(line.supermarketId);
    if (!groups[key]) {
      groups[key] = {
        storeId: line.supermarketId,
        storeName: line.supermarketName || `Store ${line.supermarketId}`,
        lines: [],
        subtotal: 0,
      };
    }
    groups[key].lines.push(line);
    groups[key].subtotal += Number(line.quantity || 0) * Number(line.unitPrice || 0);
  });
  return Object.values(groups);
}

/** Parsed in-stock limit for a catalog row or cart line; null if unknown/unlimited. */
export function getGroceryItemStockLimit(item) {
  if (!item || typeof item !== 'object') return null;
  const stock = Number(item.stockQuantity ?? item.quantityInStock);
  if (!Number.isFinite(stock) || stock < 0) return null;
  return stock;
}

export function formatGroceryStockQty(quantity) {
  const num = Number(quantity);
  if (!Number.isFinite(num)) return String(quantity ?? '');
  return num % 1 === 0 ? String(num.toFixed(0)) : String(num);
}

export function groceryStockLimitMessage(itemName, available, { requested = null } = {}) {
  const label = itemName || 'Item';
  const availText = formatGroceryStockQty(available);
  if (Number(available) <= 0) {
    return `"${label}" is out of stock.`;
  }
  if (requested != null) {
    return `Insufficient stock for "${label}": available ${availText}, requested ${formatGroceryStockQty(requested)}.`;
  }
  return `Insufficient stock for "${label}": only ${availText} available.`;
}

/**
 * Returns true if quantity is allowed; shows alert and returns false when over stock.
 */
export function validateGroceryCartQuantity(itemName, nextQty, stockLimit) {
  if (stockLimit == null) return true;
  if (nextQty <= stockLimit) return true;
  window.alert(groceryStockLimitMessage(itemName, stockLimit, { requested: nextQty }));
  return false;
}
